// ProductReviewsBlock — displays per-product reviews summary + list
// Receives stats from /api/reviews/public/by-product/{product_id}
import React, { useMemo, useState } from "react";
import { Star, ShieldCheck, CornerDownRight } from "lucide-react";
import { useSettings, useLang } from "../lib/providers";

const TENNIS_GREEN = "#a7ff00";

function StarRow({ n, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          style={{ width: size, height: size }}
          fill={i <= n ? TENNIS_GREEN : "none"}
          color={i <= n ? TENNIS_GREEN : "#666"}
          strokeWidth={1.8}
        />
      ))}
    </div>
  );
}

export default function ProductReviewsBlock({ stats }) {
  const { settings } = useSettings();
  const { lang } = useLang();
  const shopName = settings?.site_name || "DZ Tennis Shopper";
  const [showAll, setShowAll] = useState(false);

  const visibleReviews = useMemo(() => {
    const data = stats?.data || [];
    return showAll ? data : data.slice(0, 6);
  }, [stats, showAll]);

  if (!stats || stats.count === 0) return null;
  const avg = Number(stats.avg || 0);
  const dist = stats.dist || { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };

  const t = (fr, ar) => (lang === "ar" ? ar : fr);

  return (
    <section className="border border-white/10 p-5 sm:p-6 my-8" data-testid="product-reviews-block">
      <h2 className="font-heading font-black uppercase tracking-tight text-2xl mb-4">
        {t("Avis clients", "آراء العملاء")} ⭐
      </h2>
      <div className="flex flex-col md:flex-row gap-6 mb-6">
        <div className="text-center md:text-start md:w-44 shrink-0">
          <div className="text-5xl font-black text-neon leading-none">{avg.toFixed(1)}</div>
          <div className="mt-2 inline-flex"><StarRow n={Math.round(avg)} size={18} /></div>
          <div className="text-xs text-white/50 mt-1">
            {stats.count} {t(`avis vérifié${stats.count > 1 ? "s" : ""}`, "تقييم موثق")}
          </div>
        </div>
        <div className="flex-1">
          {[5, 4, 3, 2, 1].map((s) => {
            const c = dist[String(s)] || dist[s] || 0;
            const pct = stats.count ? (c / stats.count) * 100 : 0;
            return (
              <div key={s} className="flex items-center gap-2 py-0.5" data-testid={`product-reviews-dist-${s}`}>
                <span className="text-xs w-10">{s} ⭐</span>
                <div className="flex-1 h-2 bg-white/5">
                  <div className="h-full bg-neon" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-white/50 w-8 text-end">{c}</span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3" data-testid="product-reviews-list">
        {visibleReviews.map((r) => (
          <div key={r.id} className="border border-white/10 p-3 hover:border-neon/30 transition-colors" data-testid={`product-review-${r.id}`}>
            <div className="flex items-center justify-between mb-1">
              <StarRow n={r.rating} size={14} />
              <span className="inline-flex items-center gap-1 text-[9px] text-neon uppercase tracking-widest font-bold">
                <ShieldCheck className="w-3 h-3" /> {t("Vérifié", "موثق")}
              </span>
            </div>
            <p className="text-sm text-white/85 leading-relaxed">{r.comment || <em className="text-white/40">{t("Pas de commentaire.", "بدون تعليق.")}</em>}</p>
            {r.photos && r.photos.length > 0 && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {r.photos.slice(0, 4).map((url, i) => (
                  <a key={i} href={url} target="_blank" rel="noreferrer" className="w-14 h-14 overflow-hidden border border-white/10 hover:border-neon">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                  </a>
                ))}
              </div>
            )}
            <div className="text-[10px] text-white/40 mt-2 font-mono">— {r.customer_name} · {(r.created_at || "").slice(0, 10)}</div>
            {r.admin_reply && r.admin_reply.trim() && (
              <div className="mt-2 pt-2 border-t border-neon/20" data-testid={`product-review-reply-${r.id}`}>
                <div className="flex items-center gap-1 text-[9px] uppercase tracking-widest text-neon font-bold mb-0.5">
                  <CornerDownRight className="w-2.5 h-2.5" /> {t("Réponse", "رد")} — {shopName}
                </div>
                <p className="text-[11px] text-white/80 leading-snug whitespace-pre-line">{r.admin_reply}</p>
              </div>
            )}
          </div>
        ))}
      </div>

      {stats.data && stats.data.length > 6 && (
        <div className="text-center mt-4">
          <button
            onClick={() => setShowAll((v) => !v)}
            className="text-xs uppercase tracking-widest text-neon hover:underline font-bold"
            data-testid="product-reviews-toggle-all"
          >
            {showAll ? t("Réduire", "تقليل") : t(`Voir tous les ${stats.data.length} avis →`, `عرض جميع ${stats.data.length} تقييمات →`)}
          </button>
        </div>
      )}
    </section>
  );
}
